/**
 * One frame drawn as a window outside React Flow: positioned in its box,
 * the surface's frame renderer around the live frame body, drag and
 * double-click from its headers and its top band (`frame-window-band.ts`),
 * and the box the resize handles (`focus-window-resize.tsx`) show while
 * they run. Bindings decide what a gesture does: focus windows
 * (`focus-window.tsx`) write the presentation, floating windows their own
 * store.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type RefObject,
} from "react";
import type { ComponentType } from "react";
import { frameComponents } from "@/components/shapes";
import { cardPaint } from "@/components/shapes/shared/card-paint";
import type { FrameProps } from "@/components/shapes/shared/frame-props";
import {
  FrameRendererContext,
  type FrameRenderer,
} from "@/components/shapes/shared/frame-renderer-context";
import { cn } from "@/lib/cn";
import type { FrameData } from "@/lib/frame-data";
import { frameCardFamily } from "@/lib/frame-card-shape";
import { frameDisplayTitle } from "@/lib/frame-display-title";
import type { FocusWindowBox } from "@/lib/focus-window-layout";
import { isFrameHeaderDoubleClickTarget } from "@/lib/frame-window-gestures";
import { isFrameWindowBandPress } from "./frame-window-band";
import {
  FrameWindowContext,
  type FrameWindowContextValue,
  type FrameWindowTestIdPrefix,
} from "./frame-window-context";
import { useFrameWindowBandShields } from "./use-frame-window-band-shields";
import "./frame-window.css";
import "./frame-window-card.css";

/** Pointer travel before a header press becomes a drag. */
const DRAG_START_DISTANCE = 3;

/**
 * An offset on the display's pixel grid. The window is a composited layer:
 * at a fractional device pixel it is resampled and its text reads soft (a
 * window dropped from a 2x display lands on half points, half a pixel on a
 * 1x display).
 */
function onDevicePixels(value: number): number {
  const ratio = window.devicePixelRatio || 1;
  return Math.round(value * ratio) / ratio;
}

export interface SurfacePoint {
  x: number;
  y: number;
}

export interface SurfaceSize {
  width: number;
  height: number;
}

/** A pointer position in client pixels. */
export interface ClientPoint {
  clientX: number;
  clientY: number;
}

/** What a header drag does, in the coordinates of the window's surface. */
export interface FrameWindowDrag {
  /** The drag left its press point (`press` in client pixels); returns the
   *  box it moves. */
  start(
    pointer: SurfacePoint,
    box: FocusWindowBox,
    press: ClientPoint,
  ): FocusWindowBox;
  /** The box to draw for the window following the pointer, or `null` when
   *  the binding moves the window through its own store. */
  move(
    pointer: SurfacePoint,
    box: FocusWindowBox,
    surface: SurfaceSize,
  ): FocusWindowBox | null;
  end(pointer: SurfacePoint, box: FocusWindowBox, surface: SurfaceSize): void;
  /** The window unmounted before the pointer was released. */
  cancel(): void;
}

interface FrameWindowShellProps {
  testIdPrefix: FrameWindowTestIdPrefix;
  frame: FrameData;
  selected: boolean;
  box: FocusWindowBox;
  renderer: FrameRenderer;
  /** Absent: the surface moves its window itself (a desktop pin, natively)
   *  and the shell takes no press for a drag. */
  drag?: FrameWindowDrag;
  /** A press anywhere in the window. */
  onActivate: () => void;
  /** Header double-click. */
  onToggle: () => void;
  onResizeEnd: (box: FocusWindowBox) => void;
  rootRef?: RefObject<HTMLDivElement | null>;
  className?: string;
  /** Layers around the frame body (a focus window's card grow); the
   *  wrapper must stay mounted, or the frame remounts. */
  wrapBody?: (body: ReactNode) => ReactNode;
}

const FRAME_COMPONENTS: Partial<Record<string, ComponentType<FrameProps>>> =
  frameComponents;

/** Whether a press on `target` at `point` grabs the window `root`: a frame
 *  header or drag handle, or the band at the top of its paper. */
function isWindowGrab(
  target: EventTarget | null,
  point: ClientPoint,
  root: HTMLElement | null,
) {
  return (
    isFrameHeaderDoubleClickTarget(target) ||
    isFrameWindowBandPress(target, point, root)
  );
}

function surfaceOf(root: HTMLElement | null) {
  const rect = (root?.parentElement ?? root)?.getBoundingClientRect();
  return {
    left: rect?.left ?? 0,
    top: rect?.top ?? 0,
    size: { width: rect?.width ?? 0, height: rect?.height ?? 0 },
  };
}

export function FrameWindowShell({
  testIdPrefix,
  frame,
  selected,
  box,
  renderer,
  drag,
  onActivate,
  onToggle,
  onResizeEnd,
  rootRef: givenRootRef,
  className,
  wrapBody,
}: FrameWindowShellProps) {
  const ownRootRef = useRef<HTMLDivElement>(null);
  const rootRef = givenRootRef ?? ownRootRef;
  const [gestureBox, setGestureBox] = useState<FocusWindowBox | null>(null);
  const shown = gestureBox ?? box;
  // Iframes swallow presses: their band part is shielded.
  const band = useFrameWindowBandShields(rootRef);
  // Ends a drag whose window unmounts before the pointer is released.
  const abortDragRef = useRef<(() => void) | null>(null);
  useEffect(() => () => abortDragRef.current?.(), []);
  // The pointer a gesture follows; another pointer's press is ignored.
  const gesturePointerRef = useRef<number | null>(null);
  // Whether the last press grabbed the window. Pointer capture retargets the
  // double-click to the root, so the double-click reads the press.
  const pressGrabbedRef = useRef(false);

  const handlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (
        gesturePointerRef.current !== null &&
        gesturePointerRef.current !== event.pointerId
      )
        return;
      pressGrabbedRef.current =
        drag !== undefined &&
        event.button === 0 &&
        isWindowGrab(event.target, event, rootRef.current);
      if (!drag || !pressGrabbedRef.current) return;
      const binding = drag;
      event.preventDefault();
      event.currentTarget.setPointerCapture?.(event.pointerId);
      abortDragRef.current?.();
      const pointerId = event.pointerId;
      gesturePointerRef.current = pointerId;
      const surface = surfaceOf(rootRef.current);
      const toSurface = (e: ClientPoint) => ({
        x: e.clientX - surface.left,
        y: e.clientY - surface.top,
      });
      const press: ClientPoint = {
        clientX: event.clientX,
        clientY: event.clientY,
      };
      const startBox = shown;
      let moving: { box: FocusWindowBox; dx: number; dy: number } | null = null;
      let last = startBox;
      function onMove(e: PointerEvent) {
        if (e.pointerId !== pointerId) return;
        if (!moving) {
          if (
            Math.hypot(e.clientX - press.clientX, e.clientY - press.clientY) <
            DRAG_START_DISTANCE
          )
            return;
          const pressPoint = toSurface(press);
          const dragged = binding.start(pressPoint, startBox, press);
          moving = {
            box: dragged,
            dx: pressPoint.x - dragged.x,
            dy: pressPoint.y - dragged.y,
          };
        }
        const pointer = toSurface(e);
        const next = binding.move(
          pointer,
          { ...moving.box, x: pointer.x - moving.dx, y: pointer.y - moving.dy },
          surface.size,
        );
        if (!next) return;
        last = next;
        setGestureBox(next);
      }
      function onUp(e: PointerEvent) {
        if (e.pointerId !== pointerId) return;
        stop();
        if (moving) binding.end(toSurface(e), last, surface.size);
        setGestureBox(null);
      }
      function onCancel(e: PointerEvent) {
        if (e.pointerId !== pointerId) return;
        abort();
        setGestureBox(null);
      }
      function stop() {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onCancel);
        abortDragRef.current = null;
        gesturePointerRef.current = null;
      }
      function abort() {
        stop();
        if (moving) binding.cancel();
      }
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onCancel);
      abortDragRef.current = abort;
    },
    [drag, rootRef, shown],
  );

  const windowContext = useMemo<FrameWindowContextValue>(
    () => ({
      testIdPrefix,
      box: shown,
      showBox: setGestureBox,
      commitResize: onResizeEnd,
    }),
    [onResizeEnd, shown, testIdPrefix],
  );

  // The window wears its card: family (type and margins) and paint.
  const family = useMemo(
    () => frameCardFamily(frame.frameType, frame),
    [frame],
  );
  const paint = useMemo(() => cardPaint(frame), [frame]);

  const Frame = FRAME_COMPONENTS[frame.frameType];
  const body = Frame && (
    <Frame
      id={frame.frameId}
      data={frame}
      selected={selected}
      width={shown.width}
      height={shown.height}
    />
  );
  return (
    <div
      ref={rootRef}
      role="group"
      aria-label={frameDisplayTitle(frame)}
      className={cn("pointer-events-auto absolute left-0 top-0", className)}
      style={
        {
          transform: `translate3d(${onDevicePixels(shown.x)}px, ${onDevicePixels(shown.y)}px, 0)`,
          width: shown.width,
          height: shown.height,
          "--frame-paint": paint.background,
        } as CSSProperties
      }
      {...{ [`data-${testIdPrefix}`]: "" }}
      data-card-family={family}
      data-paint={paint.ink}
      data-canvas-frame-id={frame.frameId}
      data-interactive="true"
      data-testid={`${testIdPrefix}-${frame.frameId}`}
      onPointerDownCapture={() => {
        // Content may stop the press before it bubbles to the root.
        pressGrabbedRef.current = false;
        onActivate();
      }}
      onPointerDown={handlePointerDown}
      onPointerOver={band.onPointerOver}
      onPointerLeave={band.onPointerLeave}
      onDoubleClick={() => {
        if (pressGrabbedRef.current) onToggle();
      }}
    >
      <FrameWindowContext.Provider value={windowContext}>
        <FrameRendererContext.Provider value={renderer}>
          {wrapBody ? wrapBody(body) : body}
        </FrameRendererContext.Provider>
      </FrameWindowContext.Provider>
      {band.shields.map((shield, index) => (
        <div
          key={index}
          className="absolute z-[64]"
          style={shield}
          aria-hidden="true"
          data-frame-drag-handle="true"
          data-testid={`${testIdPrefix}-band-shield-${frame.frameId}`}
        />
      ))}
    </div>
  );
}
